/**
 * 阶段 1：双模式入口大厅与公开房间轮询控制器 (Mode Portal & Room Browser Controller)
 * 职责：
 * 1. 处理「竞速模式」与「无限模式」两大半透明主入口点击
 * 2. 处理房间号折叠抽屉输入与公开房间列表自动轮询 (/api/rooms)
 * 3. 解析 URL ?room=XXXXXX 邀请链接参数自动进房
 */

import type { GameMode } from "../../shared/protocol";
import { ArenaDomRefs, escapeHtml } from "../ui/domRefs";

export class ModePortalController {
  private lobbyPollTimer: number | null = null;

  constructor(
    private readonly dom: ArenaDomRefs,
    private readonly callbacks: {
      onEnterNewRoomWithMode: (mode: GameMode) => void;
      onJoinExistingRoom: (roomId: string) => void;
    }
  ) {
    this.bindEvents();
  }

  private bindEvents() {
    this.dom.elBtnPortalSpeed.addEventListener("click", () => {
      this.callbacks.onEnterNewRoomWithMode("speed_2048");
    });

    this.dom.elBtnPortal2048.addEventListener("click", () => {
      this.callbacks.onEnterNewRoomWithMode("classic_2048");
    });

    this.dom.elBtnPortalEndless.addEventListener("click", () => {
      this.callbacks.onEnterNewRoomWithMode("endless");
    });

    this.dom.elBtnRefreshRooms.addEventListener("click", () => {
      this.fetchRoomList();
    });

    this.dom.elBtnToggleJoinDrawer.addEventListener("click", () => {
      const isHidden = this.dom.elJoinDrawerRow.style.display === "none";
      this.dom.elJoinDrawerRow.style.display = isHidden ? "flex" : "none";
      if (isHidden) this.dom.elInputRoomCode.focus();
    });

    this.dom.elBtnCancelJoin.addEventListener("click", () => {
      this.dom.elJoinDrawerRow.style.display = "none";
    });

    this.dom.elInputRoomCode.addEventListener("keydown", (e) => {
      if (e.key === "Enter") this.dom.elBtnJoinRoom.click();
    });

    this.dom.elBtnJoinRoom.addEventListener("click", () => {
      const code = this.dom.elInputRoomCode.value.trim();
      if (!code) {
        alert("请输入 6 位房间号");
        return;
      }
      this.callbacks.onJoinExistingRoom(code);
    });
  }

  public showLoading(text: string) {
    if (this.dom.elLoadingOverlay) {
      this.dom.elLoadingOverlay.style.display = "flex";
      this.dom.elLoadingText.textContent = text;
    }
  }

  public hideLoading() {
    if (this.dom.elLoadingOverlay) {
      this.dom.elLoadingOverlay.style.display = "none";
    }
    if (this.dom.elBtnJoinRoom) {
      (this.dom.elBtnJoinRoom as HTMLButtonElement).disabled = false;
      this.dom.elBtnJoinText.textContent = "进入房间";
    }
  }

  public startLobbyPolling() {
    this.stopLobbyPolling();
    this.fetchRoomList();
    this.lobbyPollTimer = window.setInterval(() => {
      if (this.dom.elModalLobby.style.display !== "none") {
        this.fetchRoomList();
      }
    }, 4000);
  }

  public stopLobbyPolling() {
    if (this.lobbyPollTimer !== null) {
      clearInterval(this.lobbyPollTimer);
      this.lobbyPollTimer = null;
    }
  }

  public async fetchRoomList() {
    try {
      const res = await fetch("/api/rooms", { cache: "no-store" });
      if (!res.ok) return;
      const data = (await res.json()) as {
        rooms: Array<{
          id: string;
          hostName: string;
          gameMode?: string;
          playerCount?: number;
          createdAt: number;
        }>;
      };
      const rooms = data.rooms || [];
      this.dom.elRoomsCountBadge.textContent = String(rooms.length);

      if (rooms.length === 0) {
        this.dom.elLobbyRoomsList.innerHTML = `<div class="room-empty-card">暂无公开房间</div>`;
        return;
      }

      this.dom.elLobbyRoomsList.innerHTML = rooms
        .map((r) => {
          const agoSec = Math.max(1, Math.floor((Date.now() - r.createdAt) / 1000));
          const timeText = agoSec < 60 ? `${agoSec}秒前` : `${Math.floor(agoSec / 60)}分前`;
          const modeLabel =
            r.gameMode === "endless"
              ? "无限模式"
              : r.gameMode === "speed_2048"
                ? "极速 3:00"
                : "标准 8:00";
          const countLabel = r.playerCount ? `${r.playerCount}人在线` : "召集中";
          return `
            <div class="room-card-item">
              <div class="room-card-left">
                <div class="room-card-title">
                  <span class="room-card-code">#${r.id}</span>
                  <span class="room-card-host">房主: ${escapeHtml(r.hostName)} · ${modeLabel} (${countLabel})</span>
                </div>
                <span class="room-card-time">${timeText}</span>
              </div>
              <button class="btn-card-enter" data-room-id="${r.id}">进入席位</button>
            </div>
          `;
        })
        .join("");

      this.dom.elLobbyRoomsList.querySelectorAll(".btn-card-enter").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          const targetId = (e.currentTarget as HTMLElement).getAttribute("data-room-id");
          if (targetId) {
            this.callbacks.onJoinExistingRoom(targetId);
          }
        });
      });
    } catch (e) {
      console.warn("[Lobby] Failed to fetch rooms:", e);
    }
  }

  public checkUrlRoomParams() {
    const params = new URLSearchParams(window.location.search);
    const roomParam = params.get("room");
    if (roomParam) {
      this.callbacks.onJoinExistingRoom(roomParam);
    }
  }
}

